import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Settings, 
  Plus, 
  Pencil, 
  Trash2
} from "lucide-react";

export default function QuotasPage() {
  const [quotas, setQuotas] = useState([]);
  const [locais, setLocais] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuota, setEditingQuota] = useState(null);
  const [formData, setFormData] = useState({
    local_id: "",
    empresa_id: "",
    quota_lugares: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotasRes, locaisRes, empresasRes] = await Promise.all([
        axios.get(`${API}/quotas`),
        axios.get(`${API}/locais`),
        axios.get(`${API}/empresas`)
      ]);
      setQuotas(quotasRes.data);
      setLocais(locaisRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        local_id: formData.local_id,
        empresa_id: formData.empresa_id,
        quota_lugares: parseInt(formData.quota_lugares)
      };

      if (editingQuota) {
        await axios.put(`${API}/quotas/${editingQuota.id}`, { quota_lugares: payload.quota_lugares });
        toast.success("Quota atualizada");
      } else {
        await axios.post(`${API}/quotas`, payload);
        toast.success("Quota criada");
      }
      setDialogOpen(false);
      setEditingQuota(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar quota");
    }
  };

  const handleEdit = (quota) => {
    setEditingQuota(quota);
    setFormData({
      local_id: quota.local_id,
      empresa_id: quota.empresa_id,
      quota_lugares: quota.quota_lugares.toString()
    });
    setDialogOpen(true);
  };

  const handleDelete = async (quotaId) => {
    if (!confirm("Tem a certeza que deseja eliminar esta quota?")) return;
    try {
      await axios.delete(`${API}/quotas/${quotaId}`);
      toast.success("Quota eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar quota");
    }
  };

  const resetForm = () => {
    setFormData({ local_id: "", empresa_id: "", quota_lugares: "" });
  };

  const getLocalName = (localId) => locais.find(l => l.id === localId)?.nome || "N/A";
  const getEmpresaName = (empresaId) => empresas.find(e => e.id === empresaId)?.nome_empresa || "N/A";

  const headerAction = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) {
        setEditingQuota(null);
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="add-quota-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Quota
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingQuota ? "Editar Quota" : "Nova Quota"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Local</Label>
            <Select 
              value={formData.local_id} 
              onValueChange={(value) => setFormData({ ...formData, local_id: value })}
              disabled={!!editingQuota}
            >
              <SelectTrigger data-testid="quota-local-select">
                <SelectValue placeholder="Selecione um local" />
              </SelectTrigger>
              <SelectContent>
                {locais.map((local) => (
                  <SelectItem key={local.id} value={local.id}>{local.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Empresa</Label>
            <Select 
              value={formData.empresa_id} 
              onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
              disabled={!!editingQuota}
            >
              <SelectTrigger data-testid="quota-empresa-select">
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map((empresa) => (
                  <SelectItem key={empresa.id} value={empresa.id}>{empresa.nome_empresa}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="quota">Lugares Alocados</Label>
            <Input
              id="quota"
              type="number"
              min="1"
              value={formData.quota_lugares}
              onChange={(e) => setFormData({ ...formData, quota_lugares: e.target.value })}
              placeholder="Ex: 10"
              required
              data-testid="quota-lugares-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="quota-submit-btn">
              {editingQuota ? "Guardar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Quotas" subtitle="Gestão de quotas por empresa/local" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : quotas.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <Settings className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B] mb-4">Nenhuma quota configurada</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0035CC]">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Quota
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Local</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead className="text-center">Quota</TableHead>
                <TableHead className="text-center">Ocupação</TableHead>
                <TableHead className="text-center">Disponível</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotas.map((quota) => (
                <TableRow key={quota.id} data-testid={`quota-row-${quota.id}`}>
                  <TableCell className="font-medium">{getLocalName(quota.local_id)}</TableCell>
                  <TableCell>{getEmpresaName(quota.empresa_id)}</TableCell>
                  <TableCell className="text-center">{quota.quota_lugares}</TableCell>
                  <TableCell className="text-center">{quota.ocupacao_atual || 0}</TableCell>
                  <TableCell className="text-center">
                    <Badge 
                      variant={(quota.quota_lugares - (quota.ocupacao_atual || 0)) > 0 ? "default" : "destructive"}
                      className={(quota.quota_lugares - (quota.ocupacao_atual || 0)) > 0 ? "bg-[#00C853]" : "bg-[#D50000]"}
                    >
                      {quota.quota_lugares - (quota.ocupacao_atual || 0)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(quota)} data-testid={`edit-quota-${quota.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-[#D50000]" onClick={() => handleDelete(quota.id)} data-testid={`delete-quota-${quota.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </PageLayout>
  );
}
